import {
  IWebsiteBuilderTableBodyRow,
  TWebsiteBuilderTableRow,
  websiteBuilderGenerateTableRowDesignStyles,
} from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderTable";
import React, { useEffect, useRef } from "react";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { useAppSelector } from "hooks/redux";
import classNames from "classnames";
import { TableBuilderCellView } from "./TableBuilderCellView";
import { useTableBuilderContext } from "../TableBuilderContext";
import { useTableBuilderDomNodeRefMapRegistration } from "../hooks/useTableBuilderDomNodeRefMapRegistration";

interface ITableBuilderRowViewProps {
  row: TWebsiteBuilderTableRow;
}

interface ITableBuilderRowDragState {
  isDragging: boolean;
}

interface ITableBuilderColumnDragState {
  isDragging: boolean;
}

const ACCEPTED_TABLE_BUILDER_ROW_DRAG_ITEM = "table-builder-row-item";
const ACCEPTED_TABLE_BUILDER_COLUMN_DRAG_ITEM = "table-builder-column-item";
export const ACCEPTED_TABLE_BUILDER_DRAG_ITEMS = [ACCEPTED_TABLE_BUILDER_ROW_DRAG_ITEM, ACCEPTED_TABLE_BUILDER_COLUMN_DRAG_ITEM];

export interface ITableBuilderRowDragItem {
  type: typeof ACCEPTED_TABLE_BUILDER_ROW_DRAG_ITEM;
  bodyRow: IWebsiteBuilderTableBodyRow;
  tableNodeId: string;
}

export interface ITableBuilderColumnDragItem {
  type: typeof ACCEPTED_TABLE_BUILDER_COLUMN_DRAG_ITEM;
  columnIndex: number;
  tableNodeId: string;
}

export type TTableBuilderDragItem = ITableBuilderRowDragItem | ITableBuilderColumnDragItem;

export function isTableBuilderRowDragItem(item: any): item is ITableBuilderRowDragItem {
  return item?.type === ACCEPTED_TABLE_BUILDER_ROW_DRAG_ITEM;
}

export function isTableBuilderColumnDragItem(item: any): item is ITableBuilderColumnDragItem {
  return item?.type === ACCEPTED_TABLE_BUILDER_COLUMN_DRAG_ITEM;
}

export function TableBuilderRowView(props: ITableBuilderRowViewProps) {
  const { row } = props;

  const { selection, hoveredRowId, hoveredColumnIndex, dragLayerContext, tableNode, domNodeRefMap, operations } =
    useTableBuilderContext();

  const domNodeRefMapRegistration = useTableBuilderDomNodeRefMapRegistration(row.id, domNodeRefMap);

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const dragColumnIndexRef = useRef<number>(null);

  const designStyles = websiteBuilderGenerateTableRowDesignStyles(row);

  const isBodyRow = row.type === "body";
  const isHeadRow = row.type === "head";

  const hovered = row.id === hoveredRowId;
  const selected = selection?.type === "row" && selection.id === row.id;
  const selectedWithoutColorPicker = selected && selection.colorPickerType === "none";
  const selectedWithColorPicker = selected && selection.colorPickerType !== "none";

  const tableNodeSettings = tableNode.container.settings;

  const [columnDragState, columnDrag, columnDragPreview] = useDrag<
    ITableBuilderColumnDragItem,
    unknown,
    ITableBuilderColumnDragState
  >(() => {
    const type = ACCEPTED_TABLE_BUILDER_COLUMN_DRAG_ITEM;

    return {
      type,
      item: () => {
        return {
          type,
          columnIndex: dragColumnIndexRef.current,
          tableNodeId: tableNode.id,
        };
      },
      end: (_, monitor) => {
        if (monitor.didDrop()) {
          return;
        }

        operations.moveColumn();
      },
      collect: monitor => ({
        isDragging: !!monitor.isDragging(),
      }),
    };
  }, [
    tableNode.id,
    operations.moveColumn,
  ]);

  const [rowDragState, rowDrag, rowDragPreview] = useDrag<
    ITableBuilderRowDragItem,
    unknown,
    ITableBuilderRowDragState
  >(() => {
    const type = ACCEPTED_TABLE_BUILDER_ROW_DRAG_ITEM;

    return {
      type,
      item: () => {
        if (!isBodyRow) {
          return null;
        } else {
          return {
            type,
            bodyRow: row,
            tableNodeId: tableNode.id,
          };
        }
      },
      end: (_, monitor) => {
        if (monitor.didDrop()) {
          return;
        }

        operations.moveRow();
      },
      collect: monitor => ({
        isDragging: !!monitor.isDragging(),
      }),
    };
  }, [
    isBodyRow,
    row,
    tableNode.id,
    operations.moveRow,
  ]);

  useEffect(() => {
    columnDragPreview(getEmptyImage(), { captureDraggingState: true });
    rowDragPreview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  return (
    <tr
      ref={domNodeRefMapRegistration}
      className={classNames({
        "table-builder-row-selected-without-color-picker" : selectedWithoutColorPicker,
        "table-builder-row-selected-with-color-picker" : selectedWithColorPicker,
      })}
      style={designStyles.styleProperties}
      onMouseEnter={() => operations.hoverRow(row.id)}
      onMouseLeave={() => operations.blurRow(row.id)}
    >
      {row.cells.map((cell, cellIndex) => {
        const disableAllOptions = deviceView === "mobile";

        const headRowOptionAllowed =
          !disableAllOptions &&
          isHeadRow &&
          hovered &&
          dragLayerContext.type === "standby" &&
          cellIndex === 0;

        const rowDragAllowed =
          !disableAllOptions &&
          isBodyRow &&
          hovered &&
          ((dragLayerContext.type === "row-active" && dragLayerContext.bodyRow.id === row.id) ||
            dragLayerContext.type === "standby") &&
          cellIndex === 0;

        const columnDragAllowed =
          !disableAllOptions &&
          isHeadRow &&
          hoveredColumnIndex === cellIndex &&
          ((dragLayerContext.type === "column-active" && dragLayerContext.columnIndex === cellIndex) ||
            dragLayerContext.type === "standby");

        const mobileContentCell = isHeadRow
          ? null
          : tableNodeSettings.head.row.cells[cellIndex];

        return (
          <TableBuilderCellView
            key={cell.id}
            cell={cell}
            cellIndex={cellIndex}
            mobileContentCell={mobileContentCell}
            rowId={row.id}
            isDraggingRow={rowDragState.isDragging}
            rowDrag={rowDragAllowed ? rowDrag : null}
            isDraggingColumn={columnDragState.isDragging}
            columnDrag={columnDragAllowed ? columnDrag : null}
            dragColumnIndexRef={dragColumnIndexRef}
            headRowOptionAllowed={headRowOptionAllowed}
          />
        );
      })}
    </tr>
  );
}